import { useEffect } from 'react';
import type { JSX } from 'react';
import { createPortal } from 'react-dom';

import type { PrintDocument } from './types';
import {
  CONFIDENTIALITY_NOTICE,
  PRINT_PRODUCT_NAME,
  incompleteListBanner,
} from './helpers/buildPrintDocument';
import PrintDayTable from './PrintDayTable';
import './CalendarPrintSheet.css';

export const PRINT_ROOT_ID = 'calendar-print-root';

function getPrintRoot(): HTMLElement {
  const existing = document.getElementById(PRINT_ROOT_ID);
  if (existing) return existing;

  const root = document.createElement('div');
  root.id = PRINT_ROOT_ID;
  document.body.appendChild(root);
  return root;
}

interface CalendarPrintSheetProps {
  printDocument: PrintDocument;
  onDone: () => void;
}

/**
 * The printed document. Rendered through a portal outside #root so the print stylesheet can hide
 * the app with one rule, then handed to the browser print dialog. `onDone` fires when the dialog
 * closes, whether the user printed or cancelled.
 */
export default function CalendarPrintSheet({
  printDocument,
  onDone,
}: CalendarPrintSheetProps): JSX.Element {
  useEffect(() => {
    // Synchronising with an external system (the print dialog): wait a frame so the portal content
    // is painted, then print. `afterprint` fires on close in Chrome, Safari and Firefox.
    const frame = window.requestAnimationFrame(() => window.print());
    window.addEventListener('afterprint', onDone, { once: true });

    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('afterprint', onDone);
    };
  }, [printDocument, onDone]);

  return createPortal(<PrintSheetContent printDocument={printDocument} />, getPrintRoot());
}

interface PrintSheetContentProps {
  printDocument: PrintDocument;
}

function PrintSheetContent({ printDocument }: PrintSheetContentProps) {
  const {
    pharmacyName,
    title,
    subtitle,
    activeFilters,
    total,
    printed,
    truncated,
    generatedAt,
    generatedBy,
    days,
  } = printDocument;

  const countLabel = `${total} ${total === 1 ? 'appointment' : 'appointments'}`;
  const generatedLabel = generatedBy
    ? `Generated ${generatedAt} by ${generatedBy}`
    : `Generated ${generatedAt}`;

  return (
    <div className="vtwo:font-sans vtwo:text-print-ink">
      <header className="vtwo:flex vtwo:justify-between vtwo:items-start vtwo:border-b-2 vtwo:border-print-ink vtwo:pb-[9px] vtwo:mb-[11px]">
        <div className="vtwo:text-[14px] vtwo:font-extrabold">
          {pharmacyName}
          <span className="vtwo:block vtwo:mt-[3px] vtwo:text-[8.5px] vtwo:font-semibold vtwo:tracking-[0.14em] vtwo:uppercase vtwo:text-[#666]">
            {PRINT_PRODUCT_NAME}
          </span>
        </div>
        <div className="vtwo:text-right vtwo:text-[9px] vtwo:leading-[1.5] vtwo:text-[#333]">
          <span className="vtwo:block vtwo:mb-[2px] vtwo:text-[11.5px] vtwo:font-bold vtwo:text-print-ink">
            Appointments
          </span>
          <span className="vtwo:block">{title}</span>
          {subtitle && <span className="vtwo:block">{subtitle}</span>}
          {activeFilters.length > 0 && (
            <span className="vtwo:block">Filters: {activeFilters.join(' · ')}</span>
          )}
          <span className="vtwo:block">{countLabel}</span>
          <span className="vtwo:block">{generatedLabel}</span>
        </div>
      </header>

      {truncated && (
        <p
          role="alert"
          className="vtwo:m-0 vtwo:mb-[11px] vtwo:border-[1.5px] vtwo:border-print-ink vtwo:px-[9px] vtwo:py-[6px] vtwo:text-[9.4px] vtwo:font-bold"
        >
          {incompleteListBanner(printed, total)}
        </p>
      )}

      {days.map((day) => (
        <PrintDayTable key={day.key} day={day} />
      ))}

      <footer className="calendar-print__footer vtwo:border-t-[0.75px] vtwo:border-[#ccc] vtwo:pt-[4px] vtwo:text-[8.2px] vtwo:italic vtwo:text-print-muted">
        {pharmacyName} &nbsp;·&nbsp; {title} &nbsp;·&nbsp; {CONFIDENTIALITY_NOTICE}
      </footer>
    </div>
  );
}
